import { useState, useCallback, useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { debounce } from 'lodash';
import {
  getDocumentTemplates,
  withTeleconsultPriceList,
} from 'Redux/modules/webChat/prescription/actions';

import { ITemplate } from './types';

const PAGE_SIZE = 10;
const TEMPLATE_TYPE_ORDER = 'order';

const useSearchTemplateInfinite = (tenantId: string, serviceId?: string) => {
  const dispatch = useDispatch();

  // Get data from Redux store
  const {
    status: { documentTemplates: loadingDocumentTemplates },
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
  } = useSelector(state => state.webChat.prescription);

  const loading = loadingDocumentTemplates === 0;

  // Local state for templates, params, and search term
  const [templates, setTemplates] = useState<ITemplate[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [page, setPage] = useState<number>(1);
  const [total, setTotal] = useState<number>(0);

  const fetchData = useCallback(
    async (params: any, isReplace: boolean): Promise<void> => {
      try {
        const newParams = withTeleconsultPriceList(
          {
            page: 1,
            pageSize: PAGE_SIZE,
            type: TEMPLATE_TYPE_ORDER,
            tenantId,
            ...params,
          },
          serviceId,
        );
        const res = await dispatch(getDocumentTemplates(newParams));

        if (isReplace) {
          setTemplates(res?.data || []);
        } else {
          setTemplates(prevTemplates => [...prevTemplates, ...(res?.data || [])]);
        }

        setTotal(res?.meta?.pagination?.total || 0);
      } catch (error) {
        console.log('error', error);
      }
    },
    [dispatch, serviceId, tenantId],
  );

  const debouncedFetchData = useMemo(() => debounce(fetchData, 500), [fetchData]);

  const onChangeSearchTerm = useCallback(
    async (newSearchTerm: string): Promise<void> => {
      setTemplates([]);
      setPage(1);
      setTotal(0);
      setSearchTerm(newSearchTerm);
      await debouncedFetchData({ page: 1, searchTerm: newSearchTerm }, true);
    },
    [debouncedFetchData],
  );

  const fetchNextPage = useCallback((): void => {
    if (loading || page * PAGE_SIZE >= total) return; // Don't fetch if already loading
    const newPage = page + 1;
    setPage(newPage);
    debouncedFetchData({ page: newPage, searchTerm }, false);
  }, [debouncedFetchData, page, searchTerm, loading, total]);

  useEffect(() => {
    fetchData({ page, searchTerm }, true);
  }, []);

  return {
    templates,
    searchTerm,
    setSearchTerm,
    fetchNextPage,
    onChangeSearchTerm,
    loading,
    total,
  };
};

export default useSearchTemplateInfinite;
